// Copay declaration inside the settle sheet (GDN-2405).
//
// The shipped sheet treats a copay as an allocation *suggestion* (a chip and
// a waterfall target). This makes intent explicit: a "What is this payment?"
// segmented control above the amount, and a copay mode that pins the payment
// to a declared visit — context/today's visit preselected, recent visits one
// tap away, the eligibility-suggested amount when coverage knows it, and the
// resulting linkage stated in plain words.
//
// Pure presentational components: state, tracking, and payload mapping live
// in SettleSheet (mirrors the SettleAllocationSection split).

import { useId } from 'react';
// Direct file import (no feature barrel) — shared eligibility design tokens.
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Box,
  Chip,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  type CopayVisitOption,
  visitDateTime,
  visitDescriptor,
} from '../hooks/useRecentEncounterOptions';
import type { SettlePaymentIntent } from '../types';
import { type CopaySuggestion, formatCheckedAtTime } from '../utils/allocationEngine';

// ─── "What is this payment?" segmented control ──────────────────────────────

interface SettlePaymentIntentControlProps {
  value: SettlePaymentIntent;
  onChange: (intent: SettlePaymentIntent) => void;
  /** No visit to declare against — copay mode would be meaningless. */
  copayDisabled?: boolean;
}

export const SettlePaymentIntentControl = ({
  value,
  onChange,
  copayDisabled = false,
}: SettlePaymentIntentControlProps) => {
  const copayUnavailableId = useId();

  return (
    <>
      <Typography
        variant="overline"
        sx={{ color: ELIG_TOKENS.midnight, fontWeight: 700, display: 'block', mb: 0.5 }}
      >
        What is this payment?
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={value}
        onChange={(_, next: SettlePaymentIntent | null) => next && onChange(next)}
        sx={{
          mb: copayDisabled ? 0.5 : 1.5,
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            py: 0.5,
            fontWeight: 600,
            color: ELIG_TOKENS.inkSoft,
            '&.Mui-selected': {
              color: ELIG_TOKENS.midnight,
              bgcolor: 'rgba(28,30,133,0.08)',
              '&:hover': { bgcolor: 'rgba(28,30,133,0.12)' },
            },
          },
        }}
      >
        <ToggleButton
          value="copay"
          disabled={copayDisabled}
          aria-describedby={copayDisabled ? copayUnavailableId : undefined}
        >
          Co-pay
        </ToggleButton>
        <ToggleButton value="general">General payment</ToggleButton>
      </ToggleButtonGroup>
      {/* gdn-4176: a greyed-out option says why. "None was found" rather
          than "none exists" — the list is also empty when it failed to
          load, and the sheet cannot tell the two apart. */}
      {copayDisabled && (
        <Typography
          id={copayUnavailableId}
          variant="caption"
          sx={{ display: 'block', color: ELIG_TOKENS.inkSoft, mb: 1.5 }}
        >
          Co-pay needs a visit to link to, and none was found for this account.
        </Typography>
      )}
    </>
  );
};

// ─── Copay declaration body (amount + visit picker + linkage) ────────────────

interface SettleCopayDeclarationSectionProps {
  amountStr: string;
  onAmountChange: (value: string) => void;
  amountCents: number;
  /** Eligibility-suggested copay driving the fill-the-amount chip. */
  copaySuggestion?: CopaySuggestion | null;
  onCopayChipClick: () => void;
  visits: CopayVisitOption[];
  selectedVisitId: string | null;
  onVisitChange: (visitId: string) => void;
  isVisitsLoading?: boolean;
}

export const SettleCopayDeclarationSection = ({
  amountStr,
  onAmountChange,
  amountCents,
  copaySuggestion,
  onCopayChipClick,
  visits,
  selectedVisitId,
  onVisitChange,
  isVisitsLoading = false,
}: SettleCopayDeclarationSectionProps) => {
  const selectedVisit =
    visits.find(visit => visit.id === selectedVisitId) ?? visits[0];

  return (
    <Box>
      {/* 1 — Amount, always editable */}
      <TextField
        label="Amount"
        value={amountStr}
        onChange={e => onAmountChange(e.target.value)}
        fullWidth
        size="small"
        autoFocus
        placeholder="0.00"
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          },
        }}
        sx={{ mb: 1 }}
      />

      {/* 2 — Eligibility-suggested amount when coverage knows it */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {copaySuggestion ? (
          <>
            <Chip
              size="small"
              icon={<BoltOutlinedIcon sx={{ fontSize: 14 }} />}
              label={`Copay ${dollars(copaySuggestion.amountCents)} — suggested from coverage`}
              onClick={onCopayChipClick}
              sx={{
                bgcolor: ELIG_TOKENS.greenSoft,
                color: ELIG_TOKENS.green,
                fontWeight: 600,
                '& .MuiChip-icon': { color: ELIG_TOKENS.green },
                '&:hover': { bgcolor: ELIG_TOKENS.greenSoft, filter: 'brightness(0.96)' },
              }}
            />
            {(copaySuggestion.planName || copaySuggestion.checkedAt) && (
              <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
                {[
                  copaySuggestion.planName,
                  copaySuggestion.checkedAt
                    ? `checked ${formatCheckedAtTime(copaySuggestion.checkedAt)}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
            Coverage doesn&apos;t specify a copay — enter the amount from the
            patient&apos;s insurance card. It&apos;s still recorded as a co-pay.
          </Typography>
        )}
      </Box>

      {/* 3 — Visit picker: context/today preselected, recent visits one tap away */}
      <Typography
        variant="overline"
        sx={{ color: ELIG_TOKENS.midnight, fontWeight: 700, display: 'block', mb: 0.5 }}
      >
        For which visit?
      </Typography>
      <Box
        sx={{
          border: `1px solid ${ELIG_TOKENS.lineStrong}`,
          borderRadius: 1,
          overflow: 'hidden',
          mb: 1.5,
        }}
      >
        {visits.map(visit => (
          <VisitRow
            key={visit.id}
            visit={visit}
            selected={visit.id === selectedVisit?.id}
            onSelect={() => onVisitChange(visit.id)}
          />
        ))}
        {visits.length === 0 && (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: ELIG_TOKENS.inkFaint, px: 2, py: 1.25 }}
          >
            {isVisitsLoading ? 'Loading visits…' : 'No visits found for this patient.'}
          </Typography>
        )}
      </Box>

      {/* 4 — Resulting linkage, in plain words */}
      {selectedVisit && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            bgcolor: ELIG_TOKENS.greenTint,
            border: '1px solid rgba(4,120,85,0.22)',
            borderRadius: 1,
            px: 1.5,
            py: 1.25,
            mb: 2,
          }}
        >
          <LinkOutlinedIcon sx={{ fontSize: 16, color: ELIG_TOKENS.green, mt: '1px' }} />
          <Typography
            variant="caption"
            sx={{ color: ELIG_TOKENS.green, fontWeight: 600, lineHeight: 1.45 }}
          >
            Will record {amountCents > 0 ? `${dollars(amountCents)} ` : ''}as co-pay for{' '}
            {visitDescriptor(selectedVisit)} — reconciles automatically when insurance
            responds.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Rows & helpers ──────────────────────────────────────────────────────────

/** One selectable visit row — shared with the charge-attribution picker
 * (gdn-3086), which passes its own aria verb. */
export const VisitRow = ({
  visit,
  selected,
  onSelect,
  ariaVerb = 'Record co-pay for',
}: {
  visit: CopayVisitOption;
  selected: boolean;
  onSelect: () => void;
  ariaVerb?: string;
}) => {
  const title = [
    visitDateTime(visit),
    visit.label,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Box
      onClick={onSelect}
      // A div with role="button" takes neither focus nor Enter/Space on
      // its own — without these, keyboard users can see the rows but
      // never pick one (gdn-3086 made that a hard block: the charge
      // attribution reuses this row and gates submit on an answer).
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      role="button"
      aria-pressed={selected}
      aria-label={`${ariaVerb} ${title}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        gap: 1.25,
        alignItems: 'center',
        px: 2,
        py: 1.25,
        cursor: 'pointer',
        bgcolor: selected ? ELIG_TOKENS.greenTint : ELIG_TOKENS.card,
        '&:not(:first-of-type)': { borderTop: `1px solid ${ELIG_TOKENS.line}` },
        '&:hover': { bgcolor: selected ? ELIG_TOKENS.greenTint : ELIG_TOKENS.bg },
      }}
    >
      {selected ? (
        <CheckCircleIcon sx={{ fontSize: 18, color: ELIG_TOKENS.green }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: ELIG_TOKENS.inkFaint }} />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 600 }}>
            {title}
          </Typography>
          {(visit.isContext || isToday(visit.startsAt ?? visit.serviceDate)) && (
            <Chip
              size="small"
              label={isToday(visit.startsAt ?? visit.serviceDate) ? 'Today' : 'This visit'}
              sx={{
                height: 18,
                fontSize: 10.5,
                fontWeight: 600,
                color: ELIG_TOKENS.green,
                bgcolor: ELIG_TOKENS.greenSoft,
              }}
            />
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: ELIG_TOKENS.inkFaint,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {visit.cptCode ? `CPT ${visit.cptCode}` : 'No CPT coded yet'}
        </Typography>
      </Box>
    </Box>
  );
};

const isToday = (serviceDate: string | null): boolean => {
  if (!serviceDate) return false;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
  return serviceDate.slice(0, 10) === today;
};

const dollars = (cents: number): string => {
  const abs = Math.abs(cents);
  if (abs % 100 === 0) return `$${abs / 100}`;
  return `$${(abs / 100).toFixed(2)}`;
};
